import { queryOptions } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import { type RevenueSnapshotResponse, isRevenueSnapshotResponse } from "./revenueTypes";

export const revenueQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "revenue"] as const,
  snapshot: (date?: string) => [...revenueQueryKeys.all, "snapshot", date ?? null] as const,
};

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

export async function fetchRevenueSnapshot(date?: string): Promise<RevenueSnapshotResponse> {
  const search = new URLSearchParams();
  if (date) search.set("date", date);
  const suffix = search.size > 0 ? `?${search}` : "";
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/revenue/snapshot${suffix}`,
    { credentials: "include" },
  );
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have access to Dashboards.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(
        () => "Failed to load TIN Revenue dashboard",
      ),
    );
  }
  const body: unknown = await response.json();
  if (!isRevenueSnapshotResponse(body)) {
    throw new Error("TIN Revenue response was malformed");
  }
  return body;
}

export function revenueSnapshotQueryOptions(date?: string) {
  return queryOptions({
    queryKey: revenueQueryKeys.snapshot(date),
    queryFn: () => fetchRevenueSnapshot(date),
  });
}
